import { memo, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { LBLoader, LBXIcon } from '@linearb/buzz'
import { useAiBackfill, isConfirming, POLL_MS } from '../hooks/useAiBackfill'
import type { BackfillJob } from '../hooks/useAiBackfill'
import { AiProviderLabel } from '../custom-components/SensorsIcons'
import CompanyLogo from '../custom-components/CompanyLogo'
import { usd } from '../lib/numberFormat'
import { inclusiveDays } from '../utils/dateHelpers'
import { toneClassFor } from '../utils/aiBackfillOutcome'
import AiBackfillResultDrawer from '../views/OrganizationDetails/AiBackfillResultDrawer'
import { matchBackfillPreset } from '../views/OrganizationDetails/AiBackfillDialog'
import { PresetLabel } from '../views/OrganizationDetails/AiBackfillHistorySheet'

dayjs.extend(duration)

const JobCard = memo(function JobCard({
  job,
  now,
  onDismiss,
  onSelect
}: {
  job: BackfillJob
  now: number
  onDismiss: () => void
  onSelect: () => void
}) {
  const elapsed = dayjs
    .duration(Math.max(0, now - job.startedAt))
    .format('m:ss')
  const recovered = job.outcome?.costRecovered ?? 0
  const days = inclusiveDays(job.since, job.until)
  const preset = matchBackfillPreset(job.since, job.until, dayjs(job.startedAt))
  const confirming = isConfirming(job)
  const description = confirming
    ? `Nothing has changed yet, the job may still be writing. Checking again every ${POLL_MS / 1000} seconds.`
    : job.outcome?.description

  return (
    <div className='rounded-lg border border-border bg-background shadow-md p-3 w-[28rem] flex flex-col gap-1'>
      <div className='flex items-center gap-2'>
        <AiProviderLabel tool={job.tool} method={job.method} size={16} />
        <span className='text-xs text-muted-foreground'>· backfill</span>
        <button
          type='button'
          onClick={onDismiss}
          aria-label='Dismiss'
          className='ml-auto text-muted-foreground hover:text-foreground'
        >
          <LBXIcon className='h-4 w-4' />
        </button>
      </div>

      <div className='flex flex-col text-xs text-muted-foreground'>
        <div className='flex items-center gap-1 min-w-0'>
          <CompanyLogo website={job.orgWebsite} name={job.orgName} />
          <span className='truncate'>
            {job.orgName ? `${job.orgName} · ` : ''}Org {job.orgId}
          </span>
        </div>
        <span>
          <PresetLabel label={preset} />
          {job.since} → {job.until} · {days} days
        </span>
      </div>

      {job.state === 'running' ? (
        <div className='flex items-center gap-2 text-sm'>
          <LBLoader className='h-4 w-4' />
          <span>Running</span>
          <span className='ml-auto text-xs text-muted-foreground'>
            {elapsed} · est. {job.estimateLabel}
          </span>
        </div>
      ) : (
        <button
          type='button'
          onClick={onSelect}
          className='flex flex-col gap-0.5 text-left rounded hover:bg-muted/50 -m-1 p-1'
        >
          <div className='flex items-center gap-2 text-sm'>
            {confirming && <LBLoader className='h-4 w-4' />}
            <span className={`font-semibold ${toneClassFor(job.outcome)}`}>
              {confirming
                ? 'Confirming results'
                : (job.outcome?.label ?? 'Done')}
            </span>
            {recovered > 0 && (
              <span className='ml-auto text-sm font-medium text-green-600'>
                +{usd.format(recovered)}
              </span>
            )}
          </div>
          {description && (
            <span className='text-xs text-muted-foreground'>{description}</span>
          )}
          <div className='flex items-center justify-between mt-0.5'>
            <span className='text-xs text-blue-600'>View before / after →</span>
            {job.durationSeconds != null && (
              <span className='text-xs text-muted-foreground'>
                took {dayjs.duration(job.durationSeconds * 1000).format('m:ss')}
              </span>
            )}
          </div>
        </button>
      )}
    </div>
  )
})

// App-level dock: every in-flight / recently-finished backfill, so the operator
// can navigate away and keep watching. Fed by AiBackfillProvider.
const BackfillStatusDock: React.FC = () => {
  const { jobs, dismiss } = useAiBackfill()
  const [now, setNow] = useState(Date.now())
  const [selected, setSelected] = useState<BackfillJob | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Live 1s tick only while something is still running (for the elapsed timer).
  const anyRunning = jobs.some(j => j.state === 'running')
  useEffect(() => {
    if (!anyRunning) {
      return
    }
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [anyRunning])

  const openDrawer = (job: BackfillJob): void => {
    setSelected(job)
    setDrawerOpen(true)
  }

  if (jobs.length === 0) {
    return null
  }

  return (
    <div className='fixed bottom-4 right-4 z-50 flex flex-col gap-2'>
      {jobs.map(job => (
        <JobCard
          key={job.id}
          job={job}
          now={now}
          onDismiss={() => dismiss(job.id)}
          onSelect={() => openDrawer(job)}
        />
      ))}
      <AiBackfillResultDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        job={selected}
      />
    </div>
  )
}

export default BackfillStatusDock
